/**
 * =============================================================================
 * facets.js
 * =============================================================================
 * Collection / search filtering & sorting
 * 
 * See also:
 * - collection.js
 * - collection-grid.liquid
 * - facets.liquid
 * =============================================================================
 */

var Facets = {
  forms: undefined, // The HTML form element of the facets form

  filterData: undefined, // Cache of previous filter view requests, so we only fetch a given filter view once
  searchParamsInitial: undefined,
  searchParamsPrev: undefined,
  sectionId: undefined, // ID of the results page section, so we know what section to request via fetch
  allProductsCount: undefined,

  /**
   * Initialisation
   */
  init: function() {
    // Detect changes of radios within this element, as that means variant changed
    this.forms = document.querySelectorAll(".facets-form");
    if(this.forms.length == 0) {
      return;
    }

    this.filterData = [];
    this.searchParamsInitial = window.location.search.slice(1);
    this.searchParamsPrev = window.location.search.slice(1);
    this.sectionId = document.getElementById("products-container").dataset.sectionId;
    this.allProductsCount = document.querySelector(".products-count").innerHTML;

    this.setupEventListeners();
  },

  /**
   * Setup event listeners
   */
  setupEventListeners: function() {

    // Listen for form input changes - i.e. the facet inputs. We call the
    // listener after a 'debounce' period to allow the user some time to
    // complete their input (rather than immediately on input)
    this.debouncedOnInput = debounce((event) => {
      this.onFacetsFormInputChange(event);
    }, 500);

    this.forms.forEach(form => {
      form.addEventListener('input', this.debouncedOnInput.bind(this));
      // Listen for 'facet remove' clicks, trigger the load. We need to listen on 
      // the container, rather than directly on the buttons, as the buttons are
      // re-rendered / dynamically injected each time a filter is applied.
      form.addEventListener('click', function(event) {
        if(event.target.closest("a")?.classList.contains("facet-remove")){
          document.querySelectorAll('.products-count').forEach(element => {
            element.innerHTML = this.allProductsCount;
          });

          event.preventDefault();
          let mockedEvent = { currentTarget: event.target.closest("a"), preventDefault: () => {} };
          this.onActiveFilterClick(mockedEvent); // event has the wrong currentTarget - needs the 'a' element, but we're delegating
        };
      }.bind(this));
    })
  },

  /**
   * Handle an input change in the facets form, and render the new results
   * @param {Event} e input event 
   */
  onFacetsFormInputChange: function(e) {
    console.log(e);
    const formData = new FormData(e.target.form);
    const searchParams = new URLSearchParams(formData).toString();
    console.log('search', searchParams);
    this.renderPage(searchParams, e);
  },

  /**
   * Render the section
   * 
   * @param {String} searchParams The query string to load (filters / page)
   * @param {Event} event The original trigger event
   * @param {Boolean} updateURLHash Whether to update the URL hash with the new URL
   */
  renderPage: function(searchParams, event, updateURLHash = true) {
    this.searchParamsPrev = searchParams;
    this.showLoading(true);
    const url = `${window.location.pathname}?section_id=${this.sectionId}&${searchParams}`;
    
    // Check if we have this combo of filters in the cache. If so, use cache. If
    // not, fetch fresh from Shopify.
    const filterDataUrl = element => element.url === url;
    this.filterData.some(filterDataUrl) ? 
      this.renderSectionFromCache(filterDataUrl, event) 
      : this.renderSectionFromFetch(url, event);

    if (updateURLHash) {
      this.updateURLHash(searchParams);
    }
  },

  /**
   * Toggle the results loading overlay (collection uses a suffixed id)
   *
   * @param {Boolean} show
   */
  showLoading: function(show) {
    const loader = document.getElementById('results-loading-collection')
      || document.getElementById('results-loading');
    if (!loader) return;
    loader.classList.toggle('d-none', !show);
  },

  /**
   * Render the section from previously-stored fetch (i.e. cache)
   * 
   * @param {String} filterDataUrl The URL we previously cached
   * @param {Event} event 
   */
  renderSectionFromCache: function(filterDataUrl, event) {
    const html = this.filterData.find(filterDataUrl).html;
    this.renderFilters(html, event);
    this.renderProductGridContainer(html);
    this.renderPagination(html);
    this.showLoading(false);
    mp_lazyload_setup();
    Waypoint.refreshAll();
    var event = new CustomEvent("collection:filtered");
    document.dispatchEvent(event);
  },

  /**
   * Render the section by fetching it fresh
   * 
   * @param {String} url 
   * @param {Event} event 
   */
  renderSectionFromFetch: function(url, event) {
    fetch(url)
      .then(response => response.text())
      .then((responseText) => {
        const html = responseText;

        // Cache this response so we can load directly if the same options are
        // picked.
        this.filterData = [...this.filterData, { html, url }];

        // Functionally the same as above
        //this.filterData = this.filterData.concat([{ html, url }]);

        this.renderFilters(html, event);
        this.renderProductGridContainer(html);
        this.renderPagination(html);
        this.showLoading(false);
        mp_lazyload_setup();
        Waypoint.refreshAll();
        var event = new CustomEvent("collection:filtered");
        document.dispatchEvent(event);
      });
  },

  /**
   * Update the URL hash with new parameters
   * 
   * @param {String} searchParams 
   */
  updateURLHash: function(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  },

  /**
   * Extract and replace the facet (filter dropdowns) HTML
   * 
   * @param {String} html
   * @param {Event} event
   */
  renderFilters: function(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

    this.forms.forEach(form => {
      // Update HTML of each facet's collapsible container that might have changed
      const facets = parsedHTML.querySelectorAll(`#${form.id} .facet`);
      Array.from(facets).forEach((element) => {
        document.querySelector(`#${form.id} .facet[data-index="${element.dataset.index}"]`).innerHTML = element.innerHTML;
      });

      // Update HTML of each facet's title that might have changed
      const facetTitles = parsedHTML.querySelectorAll(`#${form.id} .facet-title`);
      Array.from(facetTitles).forEach((element) => {
        document.querySelector(`#${form.id} .facet-title[data-index="${element.dataset.index}"]`).innerHTML = element.innerHTML;
      });
    });

    // Update HTML of the 'active filters' section
    this.renderActiveFacets(parsedHTML);
  },

  /**
   * Extract and replace the active facet (filter badges) HTML
   * 
   * @param {String} html
   */
  renderActiveFacets: function(html) {
    this.forms.forEach(form => {
      const activeFacetsElement = html.querySelector(`#${form.id} .facets-active`);
      if (!activeFacetsElement) return;
      document.querySelector(`#${form.id} .facets-active`).outerHTML = activeFacetsElement.outerHTML;
    })

    //FacetFiltersForm.toggleActiveFacets(false);
  },

  /**
   * Render the results products grid
   * 
   * @param {String} html
   */
  renderProductGridContainer: function(html) {
    const searchInput = document.querySelector('[data-collection-search-input]');
    // Leave the grid alone when a text search is active — Collection re-runs
    // search on collection:filtered so filter clicks don't wipe results.
    if (searchInput && searchInput.value.trim()) {
      return;
    }

    document.getElementById('products-grid').innerHTML = new DOMParser().parseFromString(html, 'text/html').getElementById('products-grid').innerHTML;
    document.querySelectorAll('.products-count').forEach(element => {
      const countEl = new DOMParser().parseFromString(html, 'text/html').querySelector('.products-count');
      if (countEl) element.innerHTML = countEl.innerHTML;
    });
  },

  /**
   * Extract and replace the pagination HTML
   * 
   * @param {String} html
   */
  renderPagination: function(html) {
    document.getElementById('pagination').outerHTML = new DOMParser().parseFromString(html, 'text/html').getElementById('pagination').outerHTML;
  },

  /**
   * Start the process of applying a filter
   * 
   * @param {Event} event The initiating event
   */
  onActiveFilterClick(event) {
    console.log(event);
    event.preventDefault();
    //this.toggleActiveFacets();
    const url = event.currentTarget.href.indexOf('?') == -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    this.renderPage(url);
  }
}

export default Facets;